import { Service, UploadFileType } from '@anispi-reporting-system/types';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './Button';
import { ErrorMessages } from './ErrorMessages';
import type { UploadFile } from '../hooks/useForm';
import { checkFile, uploadFile as uploadFileApi } from '../lib/Api';

interface Props {
  companyName: string;
  companyNo: string;
  officeNo: string;
  title: string;
  service: Service;
  setService: React.Dispatch<React.SetStateAction<Service>>;
  setUploadFile: React.Dispatch<React.SetStateAction<UploadFile>>;
  uploadFile: UploadFile;
  uploadFileType: UploadFileType;
}

const UploadCsvFile: FunctionComponent<Props> = ({
  title,
  companyName,
  companyNo,
  officeNo,
  service,
  setUploadFile,
  uploadFile,
  uploadFileType,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<boolean | undefined>(undefined);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setLoading(true);
      setErrorMessages([]);

      try {
        if (acceptedFiles && service) {
          setUploadFile({
            filename: '',
            uploadKey: '',
          });

          const uploadFileInfo = await uploadFileApi({
            companyNo,
            officeNo,
            service,
          });

          if (!uploadFileInfo || uploadFileInfo.success === false) {
            setResult(false);

            const uploadFileIntoErrorMessages = ['ファイルのアップロードに失敗しました。'];

            if (uploadFileInfo && uploadFileInfo.errorMessages) {
              uploadFileIntoErrorMessages.push(...uploadFileInfo.errorMessages);
            }

            setErrorMessages(uploadFileIntoErrorMessages);
            return;
          }

          // ファイルのアップロード
          const response = await fetch(uploadFileInfo.url, { body: acceptedFiles[0], method: 'PUT' });

          if (response.ok) {
            // アップロードファイルのチェック
            const checkResult = await checkFile({
              companyNo,
              fileName: uploadFileInfo.fileName,
              officeNo,
              service,
              uploadFileType,
            });
            setResult(checkResult.success);

            if (checkResult.errorMessages && checkResult.errorMessages.length > 0) {
              setErrorMessages(checkResult.errorMessages);
            }

            if (checkResult.success) {
              setUploadFile({
                filename: acceptedFiles[0].name,
                uploadKey: uploadFileInfo.fileName,
              });
            }
          } else {
            setResult(false);
            setErrorMessages(['ファイルのアップロードに失敗しました。']);
          }
        }
      } catch {
        setResult(false);
        setErrorMessages(['ファイルのアップロードに失敗しました。']);
      } finally {
        setLoading(false);
      }
    },
    [companyNo, officeNo, service, setUploadFile, uploadFileType],
  );

  const { getRootProps, getInputProps } = useDropzone({ accept: { 'text/csv': ['.csv'] }, maxFiles: 1, onDrop });

  useEffect(() => {
    setUploadFile({
      filename: '',
      uploadKey: '',
    });
    setResult(undefined);
  }, [companyName, companyNo, officeNo, service, setUploadFile]);

  return (
    <Grid container spacing={2} rowSpacing={3}>
      <Grid item xs={2} className="h-16">
        <p className="inline-block">{title}</p>
      </Grid>
      <Grid item xs={10}>
        <Box className="flex flex-row items-center space-x-2">
          <Box {...getRootProps()}>
            <input {...getInputProps()} />
            <Button className="w-40" loading={loading} variant="outlined">
              アップロード
            </Button>
          </Box>

          <Box className="inline-block pl-4">
            {result === true && uploadFile.filename && <CheckCircleOutline color="success" />}
            {result === false && <ErrorOutline color="error" />}
          </Box>

          {uploadFile.filename && <Box>{uploadFile.filename}</Box>}
        </Box>
      </Grid>

      {errorMessages.length > 0 && (
        <Grid item xs={12} className="py-2">
          <ErrorMessages errorMessages={errorMessages} />
        </Grid>
      )}
    </Grid>
  );
};

export { UploadCsvFile };
